import { useState, useEffect, useCallback, useReducer } from 'react';
import { useSelector } from 'react-redux';
import Spinner from 'react-spinkit';
import MarketingProgressReport from './MarketingProgressReport';
import SortDropdown from '../components/SortDropdown';
import Button from '../components/Button';
import Tooltip from '../components/Tooltip';
import ExpandableGeneric from '../components/ExpandableGeneric';
import SpinnerOverlay from '../SpinnerOverlay';
import StudentProgressRow from './StudentProgressRow';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import { isUserHasAnyKeys } from '../GlobalFunctions';
import { getPeekavilleComprehensionData, getStruggleData } from '../BackendInterface';
import {
  compileStudentData,
  getUnitDisplayInfo,
  getStudentLists,
  dropdownOptions,
  getClassGameData,
} from './PeekavilleDataHelpers';
import { Icon, InlineIcon } from '@iconify/react';
import circlePlus from '@iconify/icons-akar-icons/circle-plus';
import circleMinus from '@iconify/icons-akar-icons/circle-minus';
import circleCheck from '@iconify/icons-akar-icons/circle-check';
import banIcon from '@iconify-icons/ion/ban';
import reportIcon from '@iconify-icons/bx/bxs-report';
import gameControllerOutline from '@iconify-icons/ion/game-controller-outline';
import bookHeart from '@iconify-icons/bx/book-heart';
import scrollIcon from '@iconify/icons-la/scroll';
import bookIcon from '@iconify/icons-akar-icons/book';
import calendarNumberOutline from '@iconify-icons/ion/calendar-number-outline';
import styles from './PeekavilleProgressReport.module.scss';
import { StudentProxyType } from '../../peekapak-types/DataProtocolTypes';
import {
  ActivityInfo,
  ClassData,
  IndividualProgress,
  ProgressData,
} from '../../peekapak-types/PeekavilleGameDataTypes';

const PeekavilleProgressReport = () => {
  const [isShowStudents, setShowStudents] = useState(true);
  const [datalessStudents, setDatalessStudents] = useState<StudentProxyType[] | never[]>([]);
  const [isShowDataless, setShowDataless] = useState(false);
  const [displayUnit, setDisplayUnit] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  const [allStudentsLoaded, setAllStudentsLoaded] = useState(false);

  const classDataReducer = (state: ClassData, payload: IndividualProgress | 'reset') => {
    if (payload === 'reset') return [];
    return [...state, payload];
  };

  const [classData, setClassData] = useReducer(classDataReducer, []);

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const userId = useSelector((state: RootState) => {
    return state.user.userProfile!.userId;
  });
  const email = useSelector((state: RootState) => {
    return state.user.userProfile!.email;
  });

  const isLocked = !isUserHasAnyKeys(['KEY_MOODBOARD', 'KEY_PEEKAPAK_MANAGEMENT', 'KEY_WIP', 'KEY_MASTER_KEY']);

  const unitInfo = getUnitDisplayInfo();
  const unitTitles: ActivityInfo[] = unitInfo[displayUnit].slice(0, 4);
  const urlBase = 'https://peekaplatform.s3.us-east-1.amazonaws.com/assets/GameInfo/Reporting_Guide_2025.pdf';

  const handleDropdownChange = (e: React.FormEvent<HTMLSelectElement>) => {
    setDisplayUnit(parseInt(e.currentTarget.value));
  };

  const handleToggleNames = () => {
    setShowStudents((prevState) => !prevState);
  };

  const toggleHiddenStudents = () => {
    setShowDataless((prevState) => !prevState);
  };

  const openGameGuide = (unitId: number, index: number) => {
    const guideUrl = urlBase + '#page=' + (index === 1 ? unitId * 2 - 1 : unitId * 2) + '#view=Fit&zoom=100';
    const windowOptions = 'width=1200,height=600,left=100,top=100';
    window.open(guideUrl, '_blank', windowOptions);
  };

  const getIconType = (type: string) => {
    if (type === 'quest') return scrollIcon;
    if (type === 'mini-game') return gameControllerOutline;
    if (type === 'daily') return calendarNumberOutline;
    if (type === 'replayable game') return gameControllerOutline;
    if (type === 'book') return bookIcon;
    return bookIcon;
  };

  const getProgressData = async (studentList: StudentProxyType[], peekavilleData: ProgressData) => {
    try {
      for (const student of studentList) {
        if (
          classData.some(
            (item) =>
              item?.studentName?.replace(/\s+/g, '') ===
              `${student.firstName} ${student.lastName || ''}`.replace(/\s+/g, ''),
          )
        )
          return null;
        const studentName = `${student.firstName} ${student.lastName || ''}`.trim();
        const studentPeekavilleData = peekavilleData.output.find(
          (el) =>
            `${el.firstName} ${el.lastName}`.replace(/\s+/g, '') ===
            `${student.firstName} ${student.lastName || ''}`.replace(/\s+/g, ''),
        );
        const thisStudentProgress = studentPeekavilleData!.gameData.progress;
        const struggleData = await getStruggleData(student.userId);
        const comprehensionData = await getPeekavilleComprehensionData(student.userId);
        const studentUnitData = compileStudentData(thisStudentProgress, struggleData, comprehensionData);
        const thisStudentData = {
          studentName: studentName,
          Progress: studentUnitData,
        };

        setClassData(thisStudentData);
      }
    } finally {
      setAllStudentsLoaded(true);
    }
  };

  const loadStudentData = useCallback(
    async (classroom) => {
      if (!classroom) return;
      try {
        const peekavilleData = (await getClassGameData(classroom.classroomId)) as ProgressData;
        const { studentsWithData, studentsWithoutData } = await getStudentLists(
          peekavilleData,
          classroom?.listOfStudentProxies,
        );
        studentsWithData.sort((a, b) => a.firstName.localeCompare(b.firstName, 'en', { sensitivity: 'base' }));
        setDatalessStudents(studentsWithoutData);
        getProgressData(studentsWithData, peekavilleData);
      } finally {
        setIsLoaded(true);
      }
    },
    [classroom],
  );

  const handleClassChange = useCallback(() => {
    setAllStudentsLoaded(false);
    setClassData('reset');
    setIsLoaded(false);
    setShowStudents(true);
    setShowDataless(false);
    loadStudentData(classroom);
  }, [classroom]);

  useEffect(() => {
    handleClassChange();
  }, [classroom]);

  return isLocked ? (
    <MarketingProgressReport key='noReports' />
  ) : (
    <div className={styles.container} key='hasAccess'>
      <SpinnerOverlay isShow={!isLoaded} />
      <div className={styles.titleBar}>
        <div className={styles.markingLegend}>
          <h2 className={styles.reportTitle}>Student Progress</h2>
          <div className={styles.legendItem}>
            <div className={styles.markingBox} style={{ backgroundColor: '#d60000' }} />
            Poor
          </div>
          <div className={styles.legendItem}>
            <div className={styles.markingBox} style={{ backgroundColor: '#ffd100' }} />
            Fair
          </div>
          <div className={styles.legendItem}>
            <div className={styles.markingBox} style={{ backgroundColor: '#69be28' }} />
            Good
          </div>
        </div>
        <SortDropdown
          options={dropdownOptions}
          onChange={handleDropdownChange}
          value={displayUnit}
          styleGroup='reports'
        />
        <div>
          <Button
            small
            onClick={() => openGameGuide(displayUnit, 1)}
            disabled={displayUnit == 0}
            className={styles.reportButton}
          >
            <div className={styles.buttonText}>
              View Reporting Guide&nbsp;&nbsp;
              <InlineIcon icon={reportIcon} width={24} />
            </div>
          </Button>
          <Button small onClick={handleToggleNames}>
            {isShowStudents ? (
              <div className={styles.buttonText} key={'hide student button'}>
                Hide Student Info&nbsp;&nbsp;
                <InlineIcon icon={banIcon} width={24} />
              </div>
            ) : (
              <div className={styles.buttonText} key={'show student button'}>
                View Student Info&nbsp;&nbsp;
                <InlineIcon icon={circleCheck} width={24} />
              </div>
            )}
          </Button>
        </div>
      </div>

      <div className={styles.studentDataContainer}>
        <div className={styles.titleRow}>
          <div className={styles.nameCell}>
            <h4>Student Name</h4>
          </div>
          {unitTitles.map((activity: ActivityInfo, index: number) => (
            <div key={activity.displayName} className={styles.titleCell}>
              <div className={styles.unitTitle}>
                {(activity.type.toLowerCase().includes('game') || activity.type.toLowerCase().includes('daily')) &&
                displayUnit != 0 ? (
                  <h4 className={styles.guideLink} onClick={() => openGameGuide(displayUnit, index)}>
                    {activity.displayName}&nbsp;
                  </h4>
                ) : (
                  <h4>{activity.displayName}&nbsp;</h4>
                )}
                <Tooltip
                  direction='top'
                  content={activity.type.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c: string) => c.toUpperCase()))}
                  color='white'
                >
                  <InlineIcon icon={getIconType(activity.type)} className={styles.unitTooltips} />
                </Tooltip>
              </div>
              <p>{activity.description}</p>
            </div>
          ))}
        </div>

        {!isLoaded ? (
          <div key='loading class data' className={styles.loadingData}>
            Loading class data...
          </div>
        ) : (
          classData?.map((studentData, index) => (
            <StudentProgressRow
              studentName={studentData?.studentName?.trim()}
              isShowStudents={isShowStudents}
              unitData={studentData?.Progress[displayUnit]}
              isFirstRow={index === 0}
              key={`${studentData?.studentName}${index} dataRow`}
            />
          ))
        )}
        {!allStudentsLoaded && (
          <div className={styles.loadingMoreStudents}>
            <Spinner />
            Loading additional students &nbsp;&nbsp;
          </div>
        )}
        {allStudentsLoaded && (
          <>
            {datalessStudents?.map((student: StudentProxyType, index) => (
              <StudentProgressRow
                studentName={`${student.firstName || ''}${' ' || ''}${student.lastName || ''}`.trim()}
                isShowStudents={isShowStudents}
                key={`${student?.username}${index} emptyRow`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default PeekavilleProgressReport;
